import type { CSSProperties, ReactNode, RefObject } from 'react';
import type { CaseInk } from './caseMark';

import { useId } from 'react';
import { PrintMarks } from '~/components/PrintMarks';
import { Label } from '~/components/Typography';
import { MARK_POLYGONS, MARK_VIEWBOX_ATTR, MARK_VIEWBOX_MIN, MARK_VIEWBOX_SPACE } from './caseMark';

// The cases are a two-ink print: the logo's cyan and red plates on paper, slightly off register.
export const CASE_FRAME_CLASS = 'border-2 border-maroon-900 bg-paper shadow-[-6px_-4px_0_var(--case-counter),9px_9px_0_var(--case-ink)]';
// The frame and the copy (`min(34vw,28rem)`) are both sized in vw but the stage padding is in rem, so
// on tablets 54vw no longer fits beside the copy: cap it at what's left after the padding and a 4.5rem gap.
export const CASE_FRAME_WIDTH = 'w-[min(54vw,56rem,calc(100vw-min(34vw,28rem)-9.5rem))] lg:w-[min(54vw,56rem,calc(100vw-min(34vw,28rem)-14.5rem))]';

interface CaseStageProps {
  rootRef: RefObject<HTMLDivElement | null>;
  ink: CaseInk;
  /** Which side the screenshot sits on from `md` up; the halftone grows out of it */
  frameSide: 'left' | 'right';
  children: ReactNode;
}

/**
 * Full-bleed paper surface for one case: grain stock, a halftone screen of the case's ink fading out
 * from behind the screenshot, and print marks. Shared by the real cases and the placeholder.
 *
 * @param props.rootRef - Ref the scroll reveal pins
 * @param props.ink - The case's plate colours
 * @param props.frameSide - Side the screenshot sits on from `md` up
 * @param props.children - The case's layers
 * @returns The case article
 */
export function CaseStage({ rootRef, ink, frameSide, children }: CaseStageProps) {
  const fadeTo = 'left' === frameSide ? 'right' : 'left';

  return (
    <article
      ref={rootRef}
      data-cursor-tone="print"
      className="relative w-full overflow-hidden bg-paper bg-grain md:flex md:h-svh md:items-center lg:h-dvh"
      style={{ '--case-ink': ink.color, '--case-counter': ink.counter } as CSSProperties}
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-y-0 z-0 hidden w-2/3 bg-[radial-gradient(var(--case-ink)_32%,transparent_36%)] bg-size-[11px_11px] opacity-60 md:block ${
          'left' === frameSide ? 'left-0' : 'right-0'
        }`}
        style={{ maskImage: `linear-gradient(to ${fadeTo}, black, transparent 85%)` }}
      />
      <PrintMarks />
      {children}
    </article>
  );
}

// Outline bands round the punched "N", innermost first, reach in mark units. Drawn as strokes, so
// each band runs from the mark's edge out to its reach and the narrower ones paint over it. Kept
// under ~15 so the gap between the N's two strokes (30–38 units) still shows the plate.
const MARK_BANDS = [
  { paint: 'counter', reach: 6 },
  { paint: 'maroon', reach: 10 },
] as const;

/**
 * Full-frame plate in the case's ink with the brand "N" punched through it, outlined in a thin band
 * of the counter ink and a maroon keyline, like the seam's rules. The reveal scales every
 * `[data-case-mark]` group up from the mark's centre and then clears the overlay.
 *
 * @param props.ink - The case's plate colours
 * @returns The masked overlay, desktop only
 */
export function CaseMarkOverlay({ ink }: { ink: CaseInk }) {
  const maskId = `case-mark-${useId().replace(/:/g, '')}`;
  const paints = {
    counter: ink.counter,
    maroon: 'var(--color-maroon-900)',
  };

  return (
    <div data-case-overlay aria-hidden className="absolute inset-0 z-10 hidden md:block">
      <svg className="h-full w-full" viewBox={MARK_VIEWBOX_ATTR} preserveAspectRatio="xMidYMid slice">
        <defs>
          <mask id={maskId}>
            <rect x={MARK_VIEWBOX_MIN.x} y={MARK_VIEWBOX_MIN.y} width={MARK_VIEWBOX_SPACE} height={MARK_VIEWBOX_SPACE} fill="white" />
            <g data-case-mark fill="black">
              {MARK_POLYGONS.map(points => (
                <polygon key={points} points={points} />
              ))}
            </g>
          </mask>
        </defs>
        <g mask={`url(#${maskId})`}>
          <rect
            x={MARK_VIEWBOX_MIN.x}
            y={MARK_VIEWBOX_MIN.y}
            width={MARK_VIEWBOX_SPACE}
            height={MARK_VIEWBOX_SPACE}
            fill={ink.color}
          />
          <g data-case-mark fill="none" strokeLinejoin="round">
            {[...MARK_BANDS].reverse().map(band => MARK_POLYGONS.map(points => (
              <polygon key={`${band.paint}-${points}`} points={points} stroke={paints[band.paint]} strokeWidth={band.reach * 2} />
            )))}
          </g>
        </g>
      </svg>
    </div>
  );
}

// Cut tape: the ends are snipped to a point, like label-maker tape.
export const TAPE_CLIP = '[clip-path:polygon(4px_0,calc(100%-4px)_0,100%_50%,calc(100%-4px)_100%,4px_100%,0_50%)]';
// Raised letters: a dark drop below and a faint highlight above.
export const EMBOSS = '[text-shadow:0_1px_0_oklch(0.12_0.03_20),0_-1px_0_oklch(1_0_0/0.25)]';
// Stuck on by hand, never quite straight.
const TAPE_TILTS = ['-rotate-[1.5deg]', 'rotate-[1deg]', '-rotate-[0.5deg]'];

export interface CaseTag {
  label: string;
  /** Short explanation shown in a popup on hover, focus or tap */
  info?: string;
}

/**
 * Popup card for a tag's info: paper, maroon rule, the case's ink as a hard offset, and a pointer
 * down to the tape. CSS-only, driven by the tag's hover and focus.
 *
 * @param props.id - Id the tape points at with `aria-describedby`
 * @param props.text - The info
 * @returns The hidden-until-hovered popup
 */
function TagInfo({ id, text }: { id: string; text: string }) {
  return (
    <span
      id={id}
      role="tooltip"
      className="pointer-events-none invisible absolute bottom-full left-0 z-20 mb-3 w-max max-w-64 translate-y-1 border-2 border-maroon-900 bg-paper px-3 py-2 text-left text-sm leading-snug text-maroon-900 opacity-0 shadow-[4px_4px_0_var(--case-ink)] transition-[opacity,translate,visibility] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/tag:visible group-hover/tag:translate-y-0 group-hover/tag:opacity-100 group-focus-visible/tag:visible group-focus-visible/tag:translate-y-0 group-focus-visible/tag:opacity-100 group-focus/tag:visible group-focus/tag:translate-y-0 group-focus/tag:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none"
    >
      {text}
      <span aria-hidden className="absolute top-full left-5 size-2.5 -translate-y-[5px] rotate-45 border-r-2 border-b-2 border-maroon-900 bg-paper" />
    </span>
  );
}

/**
 * @param props.tags - Resolved tags, each with optional hover info
 * @returns Tags as embossed label-maker tape strips; a tag with info opens a small popup on hover, focus or tap
 */
export function CaseTags({ tags }: { tags: CaseTag[] }) {
  const baseId = useId().replace(/:/g, '');

  return (
    <ul className="pointer-events-auto flex flex-wrap gap-x-2.5 gap-y-3">
      {tags.map((tag, index) => {
        const tape = (
          <span
            className={`block bg-maroon-900 bg-grain px-4 py-1.5 shadow-[inset_0_1px_0_oklch(1_0_0/0.14),inset_0_-2px_0_oklch(0.1_0.03_20/0.5)] transition-transform duration-300 ease-out ${TAPE_CLIP} ${TAPE_TILTS[index % TAPE_TILTS.length]} ${
              null != tag.info ? 'group-hover/tag:-translate-y-0.5' : ''
            }`}
          >
            <Label className={`tracking-[0.2em] text-paper ${EMBOSS}`}>{tag.label}</Label>
          </span>
        );

        if (null == tag.info) {
          return <li key={tag.label}>{tape}</li>;
        }

        const infoId = `tag-info-${baseId}-${index}`;
        return (
          <li
            key={tag.label}
            tabIndex={0}
            aria-describedby={infoId}
            data-cursor="native"
            onKeyDown={(event) => {
              if ('Escape' === event.key) {
                event.currentTarget.blur();
              }
            }}
            className="group/tag relative cursor-help rounded-[2px] outline-none focus-visible:ring-2 focus-visible:ring-maroon-900 focus-visible:ring-offset-4 focus-visible:ring-offset-paper"
          >
            {tape}
            <TagInfo id={infoId} text={tag.info} />
          </li>
        );
      })}
    </ul>
  );
}
